/**
 * Listings Redux Slice
 * Manages ticket listings for events with normalized state and caching
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getListingsByEvent } from '../util/api/supabaseApi';

// Cache TTL: 3 minutes for listings
const LISTINGS_CACHE_TTL = 3 * 60 * 1000;

// Async thunks
export const fetchListingsByEvent = createAsyncThunk(
  'listings/fetchByEvent',
  async (eventId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const cached = state.listings?.byEventId?.[eventId];

      // Return cached if fresh
      if (cached && cached.lastFetched && Date.now() - cached.lastFetched < LISTINGS_CACHE_TTL) {
        const listings = cached.listingIds.map(id => state.listings?.byId?.[id]).filter(Boolean);

        if (listings.length > 0) {
          return { eventId, listings };
        }
      }

      const listings = await getListingsByEvent(eventId);
      return { eventId, listings };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const listingsSlice = createSlice({
  name: 'listings',
  initialState: {
    byId: {}, // Normalized: { [listingId]: listing }
    byEventId: {}, // { [eventId]: { listingIds: [], lastFetched: number } }
    loading: false,
    error: null,
    // Legacy support
    listingsByEvent: {},
  },
  reducers: {
    clearListings: state => {
      state.listingsByEvent = {};
      state.byEventId = {};
      state.byId = {};
    },
    // Clean expired cache entries
    cleanExpiredCache: state => {
      const now = Date.now();
      Object.keys(state.byEventId).forEach(eventId => {
        const cached = state.byEventId[eventId];
        if (cached.lastFetched && now - cached.lastFetched > LISTINGS_CACHE_TTL) {
          // Keep the data but mark as stale (or remove if you prefer)
          delete cached.lastFetched;
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchListingsByEvent.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchListingsByEvent.fulfilled, (state, action) => {
        state.loading = false;
        const { eventId, listings } = action.payload;

        // Normalize: store listings by ID
        if (listings && listings.length > 0) {
          listings.forEach(listing => {
            // Use listing.id or generate a unique key
            const listingId = listing.id || `${eventId}-${listing.listing_id || Math.random()}`;
            state.byId[listingId] = { ...listing, id: listingId };
          });
        }

        // Store relationship
        state.byEventId[eventId] = {
          listingIds:
            listings?.map(
              (listing, idx) => listing.id || `${eventId}-${listing.listing_id || idx}`
            ) || [],
          lastFetched: Date.now(),
        };

        // Legacy support
        state.listingsByEvent[eventId] = listings;
      })
      .addCase(fetchListingsByEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearListings, cleanExpiredCache } = listingsSlice.actions;
export default listingsSlice.reducer;
